import type { RunEvent } from '@/entities/sender.entity';
import {
	formatSenderEventDetails,
	formatSenderEventLine,
	getSenderEventCategory,
	getSenderEventProgress,
	SENDER_EVENT_TYPE_LABELS,
	shouldShowSenderEventPayload,
	type SenderEventCategory,
} from '@/entities/sender-events';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle2, Info, Loader2, type LucideIcon } from 'lucide-react';

const CATEGORY_STYLES: Record<
	SenderEventCategory,
	{ dot: string; border: string; bg: string; badge: string; icon: LucideIcon; iconColor: string }
> = {
	error: {
		dot: 'bg-destructive',
		border: 'border-destructive/30',
		bg: 'bg-destructive/5',
		badge: 'bg-destructive/10 text-destructive',
		icon: AlertCircle,
		iconColor: 'text-destructive',
	},
	success: {
		dot: 'bg-chart-2',
		border: 'border-chart-2/30',
		bg: 'bg-chart-2/5',
		badge: 'bg-chart-2/10 text-chart-2',
		icon: CheckCircle2,
		iconColor: 'text-chart-2',
	},
	progress: {
		dot: 'bg-chart-4',
		border: 'border-chart-4/30',
		bg: 'bg-chart-4/5',
		badge: 'bg-chart-4/10 text-chart-4',
		icon: Loader2,
		iconColor: 'text-chart-4',
	},
	info: {
		dot: 'bg-muted-foreground/50',
		border: 'border-border',
		bg: 'bg-muted/20',
		badge: 'bg-muted text-muted-foreground',
		icon: Info,
		iconColor: 'text-muted-foreground',
	},
};

export default function RunEventCard({
	event,
	isRunActive = false,
	audienceTarget,
}: {
	event: RunEvent;
	isRunActive?: boolean;
	audienceTarget?: number;
}) {
	const category = getSenderEventCategory(event.eventType);
	const styles = CATEGORY_STYLES[category];
	const Icon = styles.icon;
	const showProgressAnimation = category === 'progress' && isRunActive;
	const line = formatSenderEventLine(event.eventType, event.payload, {
		occurredAt: event.occurredAt,
		audienceTarget,
	});
	const details = formatSenderEventDetails(event.eventType, event.payload);
	const progress = getSenderEventProgress(event.eventType, event.payload);
	const showPayload = shouldShowSenderEventPayload(event.eventType, event.payload);
	const label = SENDER_EVENT_TYPE_LABELS[event.eventType] ?? event.eventType;
	const progressPercent =
		progress != null ? Math.min(100, Math.round((progress.value / progress.max) * 100)) : null;

	return (
		<div className='flex gap-3'>
			<div className='flex flex-col items-center shrink-0 pt-1'>
				<div
					className={cn(
						'flex size-6 items-center justify-center rounded-full',
						styles.bg,
						showProgressAnimation && 'animate-pulse',
					)}
				>
					<Icon
						className={cn(
							'size-3.5',
							styles.iconColor,
							showProgressAnimation && 'animate-spin',
						)}
					/>
				</div>
				<div className='w-px flex-1 min-h-2 bg-border mt-1' />
			</div>

			<div
				className={cn(
					'flex-1 min-w-0 rounded-lg border px-3 py-2.5 text-sm mb-1',
					styles.border,
					styles.bg,
				)}
			>
				<div className='flex items-start justify-between gap-2'>
					<span className={cn('rounded-md px-2 py-0.5 text-xs font-medium shrink-0', styles.badge)}>
						{label}
					</span>
					<span className='text-xs text-muted-foreground shrink-0'>
						{new Date(event.occurredAt).toLocaleString('ru-RU')}
					</span>
				</div>

				{line ? (
					<p
						className={cn(
							'mt-1.5 whitespace-pre-wrap break-words',
							category === 'error' && 'text-destructive',
						)}
					>
						{line}
					</p>
				) : null}

				{progress != null && progressPercent != null ? (
					<div className='mt-2 flex flex-col gap-1'>
						<div className='h-1.5 rounded-full bg-muted overflow-hidden'>
							<div
								className={cn('h-full rounded-full transition-all', styles.dot)}
								style={{ width: `${progressPercent}%` }}
							/>
						</div>
						<p className='text-xs text-muted-foreground'>
							{progress.value} / {progress.max} ({progressPercent}%)
						</p>
					</div>
				) : null}

				{details ? (
					<p className='mt-1.5 text-xs text-muted-foreground whitespace-pre-wrap break-words'>{details}</p>
				) : null}

				{showPayload ? (
					<details className='mt-2'>
						<summary className='cursor-pointer text-xs text-muted-foreground'>Технические данные</summary>
						<pre className='mt-1 text-xs text-muted-foreground whitespace-pre-wrap break-all'>
							{JSON.stringify(event.payload, null, 2)}
						</pre>
					</details>
				) : null}
			</div>
		</div>
	);
}
